import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import getConfig from "next/config";

import { createGlobalStyle } from "styled-components";
import { colors } from "@src/packages/chime-ui/system";

import { useScript } from "@src/hooks/useScript";
import { useStyleSheet } from "@src/hooks/useStyleSheet";

import { track as segmentTrack } from "@src/utils/segment";
import { setupIncode } from "./incodeRequest";

const IncodeStyles = createGlobalStyle`
  .IncodeTutorialButton {
    background-color: ${colors.brightGreen} !important;
    color: ${colors.notBlack} !important;
    width: 100% !important;
  }

  .IncodeTutorialButtonContainer {
    width: 100%;
    padding: 0 23px;
  }
`;

declare global {
  interface Window {
    OnBoarding: any;
  }
}

export interface IncodeContextValue {
  incode: any;
  token: string;
  step: string;
  navigateTo: (step: string) => void;
  track: (event: string) => void;
  onComplete: Function;
}

const IncodeContext = createContext<IncodeContextValue>({} as IncodeContextValue);

interface IncodeProviderProps {
  userId: string;
  interviewId: string;
  vendorToken: string;
  onComplete: Function;
}

export function IncodeProvider({
  children,
  userId,
  interviewId,
  vendorToken,
  onComplete,
}: React.PropsWithChildren<IncodeProviderProps>) {
  useStyleSheet("https://sdk-js.s3.amazonaws.com/sdk/styles-1.6.5.css");
  const loading = useScript("https://sdk-js.s3.amazonaws.com/sdk/onBoarding-1.86.0.js");
  const [token, setToken] = useState("");
  const [incode, setIncode] = useState<string | undefined>();
  const [step, setStep] = useState("document_type");

  const init = useCallback(async () => {
    setupIncode(vendorToken, interviewId, setIncode, setToken);
  }, [interviewId, vendorToken, setIncode, setToken]);

  const track = useCallback(
    (step: string) => {
      segmentTrack("incode_session", {
        external_id: interviewId,
        user_id: userId,
        step: step,
        identity_client: true,
      });
    },
    [interviewId, userId],
  );

  const navigateTo = useCallback(
    (step: string) => {
      track(`navigate_to_${step}`);
      setStep(step);
    },
    [track],
  );

  useEffect(() => {
    if (loading) return;

    init();
  }, [loading, init]);

  return (
    <IncodeContext.Provider value={{ incode, token, step, navigateTo, track, onComplete }}>
      <IncodeStyles />
      {!!incode && children}
    </IncodeContext.Provider>
  );
}

export const useIncode = () => useContext(IncodeContext);
